import { extractExcerptFromContent, resolveBlogExcerpt } from "@/lib/blog-excerpt";

/**
 * Excerpt za PATCH pri izmjeni postojećeg članka, ili `undefined` kad se polje
 * ne šalje i server zadržava spremljeni excerpt.
 *
 * Excerpt nije polje u formi. Ako je spremljeni excerpt izveden iz tijela
 * (ili naslova), prati izmjene; ako nije, napisan je ručno (npr. WP excerpt)
 * i ne smije se prepisati prvih 200 znakova tijela.
 */
export function excerptForEdit(input: {
  previousTitle?: string | null;
  previousExcerpt?: string | null;
  previousContent?: string | object | null;
  title: string;
  content: string | object;
}): string | undefined {
  const previous = (input.previousExcerpt ?? "").trim();
  const derivedBefore = [
    resolveBlogExcerpt({ content: input.previousContent, title: input.previousTitle }),
    extractExcerptFromContent(input.previousContent),
  ];
  const isDerived =
    previous === "" || derivedBefore.includes(previous) || isPrefixOfBody(previous, input.previousContent);
  if (!isDerived) return undefined;

  const next = resolveBlogExcerpt({ content: input.content, title: input.title });
  return next === previous ? undefined : next;
}

type TextNode = { text?: string; content?: TextNode[] };

// Migrirani članci imaju excerpt iz makeExcerpt (migration/wp-html.ts): drugi rez,
// "…" na kraju, tekst svih blokova (i naslova) spojen razmacima. Takav excerpt je
// početak tijela; poredi se bez razmaka jer HTML i tiptap različito spajaju tekst.
// Goli YouTube link iz WP teksta je u excerptu ostao, a u tijelu je postao embed,
// pa se linkovi izbacuju; excerpt koji je samo link nije ničiji sažetak.
function isPrefixOfBody(excerpt: string, content: string | object | null | undefined): boolean {
  const head = compact(withoutUrls(excerpt.replace(/(?:…|\.\.\.)$/, "")));
  if (!head) return true;
  return compact(withoutUrls(allText(content))).startsWith(head);
}

function allText(content: string | object | null | undefined): string {
  if (!content) return "";
  let root: TextNode;
  try {
    root = (typeof content === "string" ? JSON.parse(content) : content) as TextNode;
  } catch {
    return "";
  }
  const parts: string[] = [];
  const walk = (node: TextNode) => {
    if (typeof node.text === "string") parts.push(node.text);
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(root);
  return parts.join(" ");
}

const compact = (text: string) => text.replace(/\s+/g, "");
const withoutUrls = (text: string) => text.replace(/https?:\/\/\S+/g, " ");
